import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import { Card, Spinner, ProgressBar } from "react-bootstrap";
import { IconButton, Tooltip } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SyncIcon from "@mui/icons-material/Sync";
import { ethers } from "ethers";
import Pagination from "../../Live/components/Pagenation";
import trinityManagerArtifact from "../../../../configs/abis/trinity-manager.json";
import config from "../../../../configs";
import { request } from "../../../../admin/plugins/axios";
import axiosInstance from "../../../../admin/plugins/axios";
import { endpoints } from "../../../../utils/networks";
import { copyToClipboard } from "../../../../Helper/Helper";
import { getUserReferrer } from "../../presaleContractFunctions";
import toast from "react-hot-toast";
import { convertToCSV, downloadCSV } from "./csvUtils";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

const { abi: trinityManagerAbi } = trinityManagerArtifact;

const shorten = (value) =>
  value ? `${value.slice(0, 6)}...${value.slice(-4)}` : "--";

const formatNumber = (value) => {
  if (!value) return "0";
  try {
    return Number(value).toLocaleString();
  } catch {
    return "0";
  }
};

const ITEMS_PER_PAGE = 10;
const SYNC_BATCH_SIZE = 50; // Number of users to fetch per batch from contract

function UsersTable({ provider, signer, refreshKey, isOwner }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [addressNames, setAddressNames] = useState({});
  const addressNamesRef = useRef({});
  const [addressEmails, setAddressEmails] = useState({});
  const addressEmailsRef = useRef({});
  const [referrers, setReferrers] = useState({}); // { userAddress: referrerAddress }
  const referrersRef = useRef({});
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState({ current: 0, total: 0 });
  const [downloadingCSV, setDownloadingCSV] = useState(false);
  const managerAddress = config.get("smartContract.TRINITY_MANAGER_ADDRESS");

  const fetchAddressNames = useCallback(async (addresses = []) => {
    if (!addresses.length) return;
    const normalized = Array.from(
      new Set(
        addresses
          .map((addr) => (typeof addr === "string" ? addr.toLowerCase() : null))
          .filter((addr) => addr && !addressNamesRef.current?.[addr])
      )
    );
    if (!normalized.length) return;
    try {
      const response = await request(
        endpoints.Users.GetNames.method,
        endpoints.Users.GetNames.url,
        { addresses: normalized }
      );
      const users = response?.data?.data?.users || [];
      const nameMapping = {};
      const emailMapping = {};
      users.forEach((user) => {
        const lower = user?.wallet_address?.toLowerCase?.();
        if (lower) {
          nameMapping[lower] =
            user?.name || user?.username || user?.wallet_address;
          if (user?.email) {
            emailMapping[lower] = user.email;
          }
        }
      });
      if (Object.keys(nameMapping).length) {
        addressNamesRef.current = {
          ...addressNamesRef.current,
          ...nameMapping,
        };
        setAddressNames((prev) => ({ ...prev, ...nameMapping }));
      }
      if (Object.keys(emailMapping).length) {
        addressEmailsRef.current = {
          ...addressEmailsRef.current,
          ...emailMapping,
        };
        setAddressEmails((prev) => ({ ...prev, ...emailMapping }));
      }
    } catch (err) {
      console.error("Failed to fetch user names", err);
    }
  }, []);

  // Sync all users from contract to API
  const syncUsers = useCallback(async () => {
    if (!provider || !signer || !managerAddress || !isOwner) {
      toast.error("Please connect with an authorized owner wallet");
      return;
    }

    setSyncing(true);
    setSyncProgress({ current: 0, total: 0 });

    try {
      const contract = new ethers.Contract(
        managerAddress,
        trinityManagerAbi,
        signer
      );

      // First, get total count
      const [, totalCount] = await contract.getAllUsersWithSharesPaginated(
        0,
        1
      );
      const total = Number(totalCount?.toString() || 0);

      if (total === 0) {
        toast.success("No users found in contract");
        setSyncing(false);
        return;
      }

      setSyncProgress({ current: 0, total });

      let allUsers = [];
      let start = 0;

      // Fetch all users in batches
      while (start < total) {
        const [usersInfo] = await contract.getAllUsersWithSharesPaginated(
          start,
          SYNC_BATCH_SIZE
        );

        // Process each user in the batch
        for (const userInfo of usersInfo) {
          const userAddress = userInfo.user;
          const totalShares = userInfo.totalShares?.toString() || "0";

          // Get referrer
          let referrerAddress = ethers.constants.AddressZero;
          try {
            referrerAddress = await getUserReferrer(provider, userAddress);
          } catch (err) {
            console.error(`Failed to get referrer for ${userAddress}`, err);
          }

          allUsers.push({
            user_address: userAddress,
            referrer_address:
              referrerAddress &&
              referrerAddress !== ethers.constants.AddressZero
                ? referrerAddress
                : "",
            total_shares: totalShares,
          });
        }

        start += usersInfo.length;
        setSyncProgress({ current: Math.min(start, total), total });
      }

      // Send all users to API
      let successCount = 0;
      let errorCount = 0;

      for (let i = 0; i < allUsers.length; i++) {
        const user = allUsers[i];
        try {
          // Use URLSearchParams for x-www-form-urlencoded format
          const formData = new URLSearchParams();
          formData.append("user_address", user.user_address);
          formData.append("referrer_address", user.referrer_address || "");
          formData.append("total_shares", user.total_shares);

          // Use axios directly for custom headers
          await axiosInstance.post(
            endpoints.TrinityUsers.Add.url,
            formData.toString(),
            {
              headers: {
                "Content-Type": "application/x-www-form-urlencoded",
              },
            }
          );

          successCount++;
        } catch (err) {
          console.error(`Failed to sync user ${user.user_address}`, err);
          errorCount++;
        }

        setSyncProgress({ current: i + 1, total: allUsers.length });
      }

      if (errorCount === 0) {
        toast.success(
          `Successfully synced ${successCount} user${
            successCount !== 1 ? "s" : ""
          }`
        );
      } else {
        toast.error(
          `Synced ${successCount} users, ${errorCount} failed. Check console for details.`
        );
      }

      // Refresh the table
      setPage(1);
    } catch (err) {
      console.error("Failed to sync users", err);
      toast.error("Failed to sync users. Check console for details.");
    } finally {
      setSyncing(false);
      setSyncProgress({ current: 0, total: 0 });
    }
  }, [provider, signer, managerAddress, isOwner]);

  const handleDownloadCSV = useCallback(async () => {
    if (!provider || !managerAddress) {
      toast.error("Please connect to the correct network.");
      return;
    }
    if (!signer || !isOwner) {
      toast.error("Please connect with an authorized owner wallet.");
      return;
    }

    setDownloadingCSV(true);
    let loadingToast = null;
    try {
      // Fetch all users from API
      let allUsers = [];
      let currentPage = 1;
      let hasMore = true;
      const perPage = 100; // Fetch in larger batches for CSV

      loadingToast = toast.loading("Fetching all users from API...");

      while (hasMore) {
        const response = await axiosInstance.get(
          endpoints.TrinityUsers.List.url,
          {
            params: {
              perPage: perPage,
              page: currentPage,
            },
          }
        );

        const users =
          response?.data?.data?.users || response?.data?.users || [];
        const paginationOptions =
          response?.data?.data?.pagination_options ||
          response?.data?.pagination_options ||
          {};

        allUsers.push(...users);

        // Check if there are more pages
        const totalCount =
          paginationOptions?.total ||
          response?.data?.data?.total ||
          response?.data?.total ||
          0;

        if (allUsers.length >= totalCount || users.length < perPage) {
          hasMore = false;
        } else {
          currentPage++;
        }
      }

      if (allUsers.length === 0) {
        toast.dismiss(loadingToast);
        toast("No users to export.");
        setDownloadingCSV(false);
        return;
      }

      // Fetch names and emails for all users
      const allAddresses = allUsers
        .map((u) => u.user_address)
        .filter((addr) => addr);
      await fetchAddressNames(allAddresses);

      // Fetch referrers for all users
      const referrerMap = {};
      for (const user of allUsers) {
        if (user.user_address) {
          try {
            const referrer = await getUserReferrer(provider, user.user_address);
            if (
              referrer &&
              referrer !== ethers.constants.AddressZero &&
              referrer !== "0x0000000000000000000000000000000000000000"
            ) {
              referrerMap[user.user_address] = referrer;
            }
          } catch (err) {
            console.error(
              `Failed to get referrer for ${user.user_address}`,
              err
            );
          }
        }
      }

      // Prepare CSV data
      const csvData = allUsers.map((user) => {
        const lower = user.user_address?.toLowerCase?.();
        const userName =
          lower && addressNamesRef.current[lower]
            ? addressNamesRef.current[lower]
            : "";
        const userEmail =
          lower && addressEmailsRef.current[lower]
            ? addressEmailsRef.current[lower]
            : "";
        const referrerAddress =
          referrerMap[user.user_address] || user.referrer_address || "";
        const referrerLower = referrerAddress?.toLowerCase?.();
        const referrerName =
          referrerLower && addressNamesRef.current[referrerLower]
            ? addressNamesRef.current[referrerLower]
            : "";

        return {
          userAddress: user.user_address || "",
          userName: userName,
          userEmail: userEmail,
          referrerAddress: referrerAddress,
          referrerName: referrerName,
          totalShares: user.total_shares || "0",
        };
      });

      const headers = [
        { key: "userAddress", label: "User Address" },
        { key: "userName", label: "User Name" },
        { key: "userEmail", label: "Email" },
        { key: "referrerAddress", label: "Referrer Address" },
        { key: "referrerName", label: "Referrer Name" },
        { key: "totalShares", label: "Total Shares" },
      ];

      const csvContent = convertToCSV(csvData, headers);
      const filename = `users_${new Date().toISOString().split("T")[0]}.csv`;
      downloadCSV(csvContent, filename);
      toast.dismiss(loadingToast);
      toast.success(`Successfully exported ${allUsers.length} users.`);
    } catch (err) {
      console.error("Failed to export CSV", err);
      if (loadingToast) toast.dismiss(loadingToast);
      toast.error("Failed to export CSV. Check console for details.");
    } finally {
      setDownloadingCSV(false);
    }
  }, [provider, signer, managerAddress, isOwner, fetchAddressNames]);

  useEffect(() => {
    console.log("🟡 UsersTable useEffect triggered", {
      provider: !!provider,
      managerAddress,
      page,
      refreshKey,
    });

    if (!provider || !managerAddress) {
      console.log("⚠️ UsersTable: Missing provider or managerAddress");
      return;
    }
    if (!signer || !isOwner) {
      setRows([]);
      setTotal(0);
      setError("Connect with an authorized owner wallet to view users.");
      return;
    }
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");

      try {
        console.log("📤 UsersTable: Fetching from API", {
          page,
          perPage: ITEMS_PER_PAGE,
        });

        // Fetch from API instead of contract
        const response = await axiosInstance.get(
          endpoints.TrinityUsers.List.url,
          {
            params: {
              perPage: ITEMS_PER_PAGE,
              page: page,
            },
          }
        );

        console.log("✅ UsersTable: API response", response?.data);

        const users =
          response?.data?.data?.users || response?.data?.users || [];

        // Get total from pagination_options
        const paginationOptions =
          response?.data?.data?.pagination_options ||
          response?.data?.pagination_options ||
          {};

        const totalCount =
          paginationOptions?.total ||
          response?.data?.data?.total ||
          response?.data?.total ||
          response?.data?.data?.totalList ||
          response?.data?.totalList ||
          0;

        console.log("📊 UsersTable: Pagination info", {
          usersCount: users.length,
          totalCount,
          page,
          perPage: ITEMS_PER_PAGE,
          paginationOptions,
        });

        const parsed = users.map((user, index) => ({
          id: user.id || `${(page - 1) * ITEMS_PER_PAGE + index}`,
          user: user.user_address || user.user,
          totalShares:
            user.total_shares?.toString() ||
            user.totalShares?.toString() ||
            "0",
          referrer: user.referrer_address || user.referrer || null,
        }));

        // Update referrers from API data
        const newReferrers = {};
        parsed.forEach((user) => {
          if (user.referrer && user.referrer !== ethers.constants.AddressZero) {
            newReferrers[user.user] = user.referrer;
            referrersRef.current[user.user] = user.referrer;
          }
        });

        if (!cancelled && Object.keys(newReferrers).length > 0) {
          setReferrers((prev) => ({ ...prev, ...newReferrers }));
        }

        // Fetch names for users and referrers
        const allAddresses = [
          ...parsed.map((u) => u.user),
          ...Object.values(newReferrers).filter(Boolean),
        ];
        if (allAddresses.length > 0) {
          fetchAddressNames(allAddresses);
        }

        console.log("✅ UsersTable: Parsed data", {
          parsedCount: parsed.length,
          parsed,
        });

        if (!cancelled) {
          setRows(parsed);
          setTotal(Number(totalCount) || 0);
          console.log("✅ UsersTable: State updated", {
            rowsCount: parsed.length,
            total: Number(totalCount) || 0,
          });
        }
      } catch (err) {
        console.error("❌ UsersTable: Failed to fetch users from API", err);
        console.error("❌ UsersTable: Error details", {
          message: err?.message,
          response: err?.response?.data,
          status: err?.response?.status,
        });
        if (!cancelled) {
          setError("Unable to fetch users from API. Try syncing first.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    provider,
    signer,
    refreshKey,
    page,
    managerAddress,
    isOwner,
    fetchAddressNames,
  ]);

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  const body = useMemo(() => {
    const colSpan = 5;
    if (!provider || !managerAddress) {
      return (
        <tr>
          <td className="table-no-data" colSpan={colSpan}>
            Connect to the correct network to view users.
          </td>
        </tr>
      );
    }
    if (!signer || !isOwner) {
      return (
        <tr>
          <td className="table-no-data" colSpan={colSpan}>
            Connect with an authorized owner wallet to view users.
          </td>
        </tr>
      );
    }
    if (loading) {
      return (
        <tr>
          <td className="table-no-data" colSpan={colSpan}>
            Loading users...
          </td>
        </tr>
      );
    }
    if (error) {
      return (
        <tr>
          <td className="table-no-data" colSpan={colSpan}>
            {error}
          </td>
        </tr>
      );
    }
    if (!rows.length) {
      return (
        <tr>
          <td className="table-no-data" colSpan={colSpan}>
            No users found.
          </td>
        </tr>
      );
    }

    return rows.map((row) => {
      const lower = row.user?.toLowerCase?.();
      const userName = lower ? addressNames[lower] : null;
      const userEmail = lower ? addressEmails[lower] : null;
      const referrerAddress =
        referrers[row.user] || referrersRef.current[row.user];
      const referrerLower = referrerAddress?.toLowerCase?.();
      const referrerName = referrerLower ? addressNames[referrerLower] : null;

      return (
        <tr key={row.id}>
          <td>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 6 }}>
              <Tooltip title={row.user || "--"} placement="top" arrow>
                <span style={{ fontSize: "0.85rem" }}>{shorten(row.user)}</span>
              </Tooltip>
              {row.user && (
                <IconButton
                  className="totalPriceH2"
                  size="small"
                  onClick={() => copyToClipboard(row.user)}
                >
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </div>
          </td>
          <td>{userName || "--"}</td>
          <td>{userEmail || "--"}</td>
          <td>
            {referrerAddress &&
            referrerAddress !== ethers.constants.AddressZero ? (
              <div style={{ display: "flex", alignItems: "flex-end", gap: 6 }}>
                <Tooltip title={referrerAddress || "--"} placement="top" arrow>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      lineHeight: 1.1,
                    }}
                  >
                    {referrerName && (
                      <span
                        style={{
                          fontSize: "1rem",
                          color: "#bfc3d9",
                          marginBottom: 4,
                        }}
                      >
                        {referrerName}
                      </span>
                    )}
                    <span style={{ fontSize: "0.75rem" }}>
                      {shorten(referrerAddress)}
                    </span>
                  </div>
                </Tooltip>
                {referrerAddress && (
                  <IconButton
                    className="totalPriceH2"
                    size="small"
                    onClick={() => copyToClipboard(referrerAddress)}
                  >
                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </div>
            ) : (
              "--"
            )}
          </td>
          <td>{formatNumber(row.totalShares)}</td>
        </tr>
      );
    });
  }, [
    rows,
    loading,
    error,
    provider,
    managerAddress,
    signer,
    isOwner,
    addressNames,
    addressEmails,
    referrers,
  ]);

  return (
    <>
      <Card className="bg-live p-2 table-container">
        <Card.Body>
          <div className="contract-summary-panel-head">
            <div className=" w-100">
              <div className="d-flex align-items-center justify-content-between gap-3">
                <h5 className="mb-0 min-w-250px">All Users</h5>

                {syncing && syncProgress.total > 0 && (
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "0rem",
                        color: "#bfc3d9",
                        fontSize: "0.875rem",
                        gap: "10px",
                      }}
                    >
                      <span>
                        Syncing users: {syncProgress.current} /{" "}
                        {syncProgress.total}
                      </span>
                      <span>
                        {Math.round(
                          (syncProgress.current / syncProgress.total) * 100
                        )}
                        %
                      </span>
                    </div>
                    <ProgressBar
                      now={(syncProgress.current / syncProgress.total) * 100}
                      label={`${Math.round(
                        (syncProgress.current / syncProgress.total) * 100
                      )}%`}
                      style={{ height: "1rem", width: "250px" }}
                    />
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    gap: "0.5rem",
                    alignItems: "center",
                  }}
                >
                  <button
                    type="button"
                    className="summary-copy-btn1"
                    onClick={handleDownloadCSV}
                    disabled={
                      downloadingCSV || !isOwner || !signer || total === 0
                    }
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      opacity:
                        downloadingCSV || !isOwner || !signer || total === 0
                          ? 0.6
                          : 1,
                      cursor:
                        downloadingCSV || !isOwner || !signer || total === 0
                          ? "not-allowed"
                          : "pointer",
                    }}
                  >
                    {downloadingCSV ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          style={{ width: "1rem", height: "1rem" }}
                        />
                        <span>Exporting...</span>
                      </>
                    ) : (
                      <>
                        <FileDownloadIcon sx={{ fontSize: 18 }} />
                        <span>Download CSV</span>
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="summary-copy-btn1 ml-3"
                    onClick={syncUsers}
                    disabled={syncing || !isOwner || !signer}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    {syncing ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          style={{ width: "1rem", height: "1rem" }}
                        />
                        <span>Syncing...</span>
                      </>
                    ) : (
                      <>
                        <SyncIcon sx={{ fontSize: 18 }} />
                        <span>Sync from Contract</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
              <div className="borderSpace borderSpaceTitle title-inbox mt-4 "></div>
            </div>
          </div>
          {/* <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
            }}
          >
            <h5 className="mb-0" style={{ color: "#fff" }}>
              Users
            </h5>
           
          </div> */}

          <div className="table-responsive-wrapper">
            <table className="purchasing-table">
              <thead>
                <tr>
                  <th>User Address</th>
                  <th>User Name</th>
                  <th>Email</th>
                  <th>Sponsor By</th>
                  <th>Total Shares</th>
                </tr>
              </thead>
              <tbody>{body}</tbody>
            </table>
          </div>
        </Card.Body>
      </Card>
      {total > 0 && totalPages > 0 && (
        <div style={{ marginTop: "1rem" }}>
          <Pagination
            totalList={total}
            pages={totalPages}
            currentPage={page}
            setCurrentPage={setPage}
            itemsPerPage={ITEMS_PER_PAGE}
          />
        </div>
      )}
    </>
  );
}

export default UsersTable;
